import React from "react";
import { Admin, Resource } from "react-admin";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";

// Auth
import { AuthGuard, authProvider, LoginPage, OAuthCallback } from "./auth";

// Components
import { Dashboard } from "./components/Dashboard";
import { UserList, UserEdit, UserShow, UserCreate } from "./components/users";
import {
  ChallengeList,
  ChallengeEdit,
  ChallengeShow,
  ChallengeCreate,
} from "./components/challenges";
import { OrderList, OrderEdit, OrderShow } from "./components/orders";
import {
  SupplementList,
  SupplementEdit,
  SupplementShow,
  SupplementCreate,
} from "./components/supplements";
import { PaymentList, PaymentShow } from "./components/payments";
import {
  ChallengeUpdateList,
  ChallengeUpdateShow,
} from "./components/challengeUpdates";
import { ChatMessageList, ChatMessageShow } from "./components/chatMessages";
import {
  SubscriptionPlanList,
  SubscriptionPlanCreate,
  SubscriptionPlanEdit,
  SubscriptionPlanShow,
  SubscriptionLimiterList,
  SubscriptionLimiterCreate,
  SubscriptionLimiterEdit,
  SubscriptionLimiterShow,
  UserSubscriptionList,
  UserSubscriptionCreate,
  UserSubscriptionEdit,
  UserSubscriptionShow,
  SubscriptionStats,
} from "./components/subscriptions";
import {
  NotificationList,
  NotificationShow,
  NotificationQueueMonitor,
  NotificationStats,
} from "./components/notifications";
import {
  AuthUserList,
  AuthUserShow,
  AuthUserEdit,
} from "./components/authUsers";
import {
  OAuthClientList,
  OAuthClientShow,
  OAuthClientCreate,
  OAuthClientEdit,
} from "./components/oauthClients";

import { theme } from "./theme";
import { dataProvider } from "./dataProvider";
import { i18nProvider } from "./i18n";
import { CustomLayout } from "./components/CustomLayout";

// QueryClient с отключенным retry
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

/**
 * Admin Panel - Protected by AuthGuard
 */
const AdminPanel = () => (
  <Admin
    dataProvider={dataProvider}
    authProvider={authProvider}
    i18nProvider={i18nProvider}
    layout={CustomLayout}
    dashboard={Dashboard}
    theme={theme}
    title="SmarChat Admin"
    queryClient={queryClient}
    disableTelemetry
  >
    {/* Auth Service */}
    <Resource
      name="auth-users"
      list={AuthUserList}
      edit={AuthUserEdit}
      show={AuthUserShow}
      options={{ label: "Пользователи (Auth)" }}
    />
    <Resource
      name="oauth-clients"
      list={OAuthClientList}
      edit={OAuthClientEdit}
      show={OAuthClientShow}
      create={OAuthClientCreate}
      options={{ label: "OAuth Клиенты" }}
    />

    {/* Break3 Backend */}
    <Resource
      name="users"
      list={UserList}
      edit={UserEdit}
      show={UserShow}
      create={UserCreate}
      options={{ label: "Пользователи" }}
    />
    <Resource
      name="challenges"
      list={ChallengeList}
      edit={ChallengeEdit}
      show={ChallengeShow}
      create={ChallengeCreate}
      options={{ label: "Челленджи" }}
    />
    <Resource
      name="challengeUpdates"
      list={ChallengeUpdateList}
      show={ChallengeUpdateShow}
      options={{ label: "Обновления челленджей" }}
    />
    <Resource
      name="supplementOrders"
      list={OrderList}
      edit={OrderEdit}
      show={OrderShow}
      options={{ label: "Заказы" }}
    />
    <Resource
      name="supplements"
      list={SupplementList}
      edit={SupplementEdit}
      show={SupplementShow}
      create={SupplementCreate}
      options={{ label: "Добавки" }}
    />
    <Resource
      name="paymentTransactions"
      list={PaymentList}
      show={PaymentShow}
      options={{ label: "Платежи" }}
    />
    <Resource
      name="chatMessages"
      list={ChatMessageList}
      show={ChatMessageShow}
      options={{ label: "Сообщения чата" }}
    />
    <Resource
      name="subscriptionPlans"
      list={SubscriptionPlanList}
      edit={SubscriptionPlanEdit}
      show={SubscriptionPlanShow}
      create={SubscriptionPlanCreate}
      options={{ label: "Планы подписок" }}
    />
    <Resource
      name="subscriptionLimiters"
      list={SubscriptionLimiterList}
      edit={SubscriptionLimiterEdit}
      show={SubscriptionLimiterShow}
      create={SubscriptionLimiterCreate}
      options={{ label: "Лимитеры подписок" }}
    />
    <Resource
      name="userSubscriptions"
      list={UserSubscriptionList}
      edit={UserSubscriptionEdit}
      show={UserSubscriptionShow}
      create={UserSubscriptionCreate}
      options={{ label: "Подписки пользователей" }}
    />
    <Resource
      name="subscriptionStats"
      list={SubscriptionStats}
      options={{ label: "Статистика подписок" }}
    />
    <Resource
      name="notifications"
      list={NotificationList}
      show={NotificationShow}
      options={{ label: "Уведомления" }}
    />
    <Resource
      name="notificationQueueMonitor"
      list={NotificationQueueMonitor}
      options={{ label: "Очередь уведомлений" }}
    />
    <Resource
      name="notificationStats"
      list={NotificationStats}
      options={{ label: "Статистика уведомлений" }}
    />
  </Admin>
);

/**
 * App - Simple routing with AuthGuard
 * 
 * KISS Architecture:
 * - /login - Login page (public)
 * - /auth-callback - OAuth callback (public)
 * - /* - Admin panel (protected by AuthGuard)
 */
const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth-callback" element={<OAuthCallback />} />
      <Route
        path="/*"
        element={
          <AuthGuard>
            <AdminPanel />
          </AuthGuard>
        }
      />
    </Routes>
  </BrowserRouter>
);

export default App;
